import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Volume2, VolumeX, X, ChevronDown, MapPin, Users, Hammer, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getImages } from '../services/api';
import { useLanguage } from '../context/LanguageContext';

const HERO_NAMES = [
  'Beau Wilfong',
  'Murray & Hellen Brown',
  'Hank & Claire Lee',
  'Ps. Gervacio & Alejandra Ceja',
  'Manantial De Vida',
  'Carlos Canela',
  'Christopher Tenney',
  'Robert & Katherine Guyer',
  'Manoj & Jessil George',
  'Samuel Baltazar',
  'Michael Williams',
  'Debbie Reed',
  'Jon Heise',
  'Gordon Pennington',
  'Greg & Heidi Blazs',
  'Collin Austin',
  'Ps. Guillermo & Milagros',
  'Ps. Nikolas & Fe Pavlov',
  'Jack & Cindy Gennaro',
  'Dr. John & Arlene',
  'Robert Alexander',
  'Mike & Christina Lopez',
  'Matt & Kathryn Timmons',
  'Mark Haywood',
  'Andrew & Irene Lewis',
  'John & Ann Gennaro',
  'Connor & Rachel Trott',
  'Terry & Heidi Tomlin',
  'Noah Brook',
  'Lucia Madey',
  'Roland Rodriguez',
  'Melissa King',
  'Eric Leightman',
  'Douglas Woolley',
  'Rusty Russell',
  'Jorge Thomas',
  'Phyllis Valenti',
  'Ricardo Sagarra',
  'Bob & Kathy Fowler',
  'Tom McGarry',
  'Sherod Keen',
  'John B McLane',
  'Patrice Schaff',
  'Artie Equihua',
  'Kenny & Elaine Gennaro',
  'Adam Faurot',
  'John Madey',
  'Stephen Flanigan',
  'Gina & Tylre Butler',
  'Joel Duarte',
  'Jose Figueroa',
  'Manny Garcia',
  'Brian Martin',
  'Lawrence Bell',
  'Justin Harden',
  'Shawn Maynard',
  'Jon Heise',
  'Marie Howard',
  'Ps. George Brantly',
  'Johnny Rez',
  'Manoj K George',
  'Sandra Bohorquez',
  'Daniel Hartless',
  'Robert Guyer',
  'Tony & Minnie Gennaro',
  'Dennis Miller',
  'James Fuglaar',
  'Joseph Di Bartolo',
  'Donald Kahler',
  'John Oslager',
];

const MISSION_ACTIVITIES = [
  {
    icon: Users,
    en: 'Work alongside our staff and children',
    es: 'Trabaja junto a nuestro personal y los niños',
  },
  {
    icon: Sparkles,
    en: 'Lead activities, games, and worship',
    es: 'Dirige actividades, juegos y adoración',
  },
  {
    icon: Hammer,
    en: 'Help with construction and improvement projects',
    es: 'Ayuda con proyectos de construcción y mejora',
  },
  {
    icon: Heart,
    en: 'Share your talents and skills',
    es: 'Comparte tus talentos y habilidades',
  },
];

const HerosPage = () => {
  const [photos, setPhotos] = useState([]);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioLoaded, setAudioLoaded] = useState(false);
  const audioRef = useRef(null);
  const { language } = useLanguage();

  useEffect(() => {
    getImages('img_sponsors_pics')
      .then(imgs => setPhotos(imgs.filter(p => p.published !== false)))
      .catch(() => {});
  }, []);

  const toggleAudio = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audioPlaying) {
      audio.pause();
      setAudioPlaying(false);
    } else {
      audio.play().then(() => setAudioPlaying(true)).catch(() => {});
    }
  };

  const en = language === 'en';

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white overflow-hidden" data-testid="heros-page">
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        loop
        preload="none"
        onCanPlay={() => setAudioLoaded(true)}
        src="https://cdn.pixabay.com/audio/2022/01/18/audio_d0a13f69d2.mp3"
      />

      {/* Audio toggle — fixed bottom-right */}
      {audioLoaded && (
        <button
          onClick={toggleAudio}
          className="fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full bg-white/10 backdrop-blur-lg border border-white/20 flex items-center justify-center hover:bg-white/20 transition-all duration-300 group"
          data-testid="audio-toggle"
          aria-label={audioPlaying ? 'Mute soundtrack' : 'Play soundtrack'}
        >
          {audioPlaying ? (
            <Volume2 className="w-5 h-5 text-amber-400 group-hover:scale-110 transition-transform" />
          ) : (
            <VolumeX className="w-5 h-5 text-white/60 group-hover:text-white transition-colors" />
          )}
        </button>
      )}

      {/* ══════════════════════════════════════════════
          SECTION 1 — CINEMATIC HERO
      ══════════════════════════════════════════════ */}
      <section className="relative flex items-center justify-center pb-0" data-testid="heros-hero-section">
        {/* Atmospheric BG */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#0d1117] to-[#0a0a0a]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-amber-500/5 blur-[150px]" />
          <div className="absolute top-1/4 right-1/4 w-[400px] h-[400px] rounded-full bg-sky-500/5 blur-[120px]" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center pt-20 pb-0">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: 'easeOut' }}
          >
            {/* Main headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black leading-[0.95] mb-6" data-testid="heros-headline">
              <span className="bg-gradient-to-r from-amber-300 via-amber-400 to-orange-400 bg-clip-text text-transparent">
                {en ? 'The Heroes' : 'Los Héroes'}
              </span>
            </h1>

            <p className="text-base sm:text-lg text-white/50 max-w-2xl mx-auto leading-relaxed mt-8 font-light" data-testid="heros-subtext">
              {en
                ? 'Every child at The Safe House is waiting for a hero — and it could be you. Whether you give financially, sponsor a child, or join us on a mission trip to Colombia, you have the power to rewrite a child\'s story forever. This page celebrates "The Heroes" who said yes to help make an impact in this mission!'
                : 'Cada niño de The Safe House está esperando un héroe — y podrías ser tú. Ya sea que dones, apadrines a un niño o nos acompañes en un viaje misionero a Colombia, tienes el poder de reescribir la historia de un niño para siempre. ¡Esta página celebra a "Los Héroes" que dijeron sí para ayudar a generar un impacto en esta misión!'}
            </p>
            <p className="mt-4 text-lg sm:text-xl font-bold text-amber-400 whitespace-nowrap" data-testid="heros-next-line">
              {en ? 'Will you be next?' : '¿Serás el siguiente?'}
            </p>
          </motion.div>

          {/* ═══ TEMPORARILY HIDDEN — Donor Names Wall (per Angelo, keep on hand for future re-enable; HERO_NAMES data is preserved above) ═══
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.4 }}
            className="mt-6 mx-auto px-0 mb-0"
            data-testid="heros-names-wall"
          >
            <div className="relative border-y sm:border border-white/[0.06] rounded-none sm:rounded-xl bg-white/[0.02] backdrop-blur-sm px-1 py-3 sm:p-5 md:p-6">
              <p className="text-[9px] sm:text-xs text-white/30 text-center mb-3 px-2 leading-relaxed italic">
                {en
                  ? 'The following people have donated at least once over the past 3 years in helping the mission in Colombia.'
                  : 'Las siguientes personas han donado al menos una vez en los últimos 3 años para ayudar a la misión en Colombia.'}
              </p>
              <div className="flex items-center gap-2 mb-2 px-1">
                <div className="h-px flex-1 bg-gradient-to-r from-transparent to-amber-400/20" />
                <Heart className="w-3 h-3 text-amber-400/60" fill="currentColor" />
                <span className="text-xs sm:text-sm tracking-[0.25em] uppercase text-amber-400/50 font-semibold">
                  {en ? 'Our Heros' : 'Nuestros Héroes'}
                </span>
                <Heart className="w-3 h-3 text-amber-400/60" fill="currentColor" />
                <div className="h-px flex-1 bg-gradient-to-l from-transparent to-amber-400/20" />
              </div>

              <div className="grid grid-cols-3 gap-x-0 gap-y-0">
                {HERO_NAMES.map((name, i) => (
                  <div
                    key={name}
                    className="text-[8px] sm:text-[13px] text-white/40 hover:text-amber-300/70 transition-colors duration-500 cursor-default text-center leading-none py-[3px] whitespace-nowrap overflow-hidden text-ellipsis px-[2px]"
                    data-testid={`hero-name-${i}`}
                  >
                    {name}
                  </div>
                ))}
              </div>

              <div className="mt-1 text-center">
                <p className="text-sm sm:text-base text-white/30 hover:text-amber-300/70 transition-colors duration-500 cursor-default italic">
                  {en
                    ? '"Your name could be here. Your legacy starts with a single act of love."'
                    : '"Su nombre podría estar aquí. Su legado comienza con un solo acto de amor."'}
                </p>
              </div>
            </div>
          </motion.div>
          ═══ END TEMPORARILY HIDDEN Names Wall ═══ */}
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          SECTION 3 — EXPERIENCE THE MISSION
      ══════════════════════════════════════════════ */}
      <section className="relative py-0 overflow-hidden mt-0" data-testid="heros-mission-section">
        <div className="absolute inset-0 bg-[#0a0a0a]" />
        {/* Accent glows */}
        <div className="absolute top-0 left-1/3 w-[500px] h-[500px] rounded-full bg-sky-500/[0.03] blur-[140px]" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] rounded-full bg-amber-500/[0.03] blur-[120px]" />

        <div className="relative z-10 max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 1 }}
            className="text-center mb-4"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black leading-tight mb-4" data-testid="mission-headline">
              <span className="text-white/90">{en ? 'Experience' : 'Vive'}</span>
              <br />
              <span className="bg-gradient-to-r from-sky-300 via-sky-400 to-blue-400 bg-clip-text text-transparent">
                {en ? 'the Mission' : 'la Misión'}
              </span>
            </h2>

            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-sky-400/20 bg-sky-400/5 mb-6">
              <MapPin className="w-3.5 h-3.5 text-sky-400" />
              <span className="text-xs tracking-[0.2em] uppercase text-sky-300/70 font-medium">
                {en ? 'Colombia, South America' : 'Colombia, Sudamérica'}
              </span>
            </div>

            <p className="text-base sm:text-lg text-white/45 max-w-2xl mx-auto leading-relaxed mb-4">
              {en
                ? 'There is something life-changing about experiencing this mission in person. Walking through the doors of our Rescue Center, meeting the children, and witnessing firsthand the hope and love your presence brings is something that will remain in your heart forever.'
                : 'Hay algo que cambia la vida al experimentar esta misión en persona. Caminar por las puertas de nuestro Centro de Rescate, conocer a los niños, y ser testigo de primera mano de la esperanza y el amor que tu presencia trae es algo que permanecerá en tu corazón para siempre.'}
            </p>

            <p className="text-base text-white/35 max-w-xl mx-auto leading-relaxed">
              {en
                ? 'We welcome teams throughout the year to join us in Colombia for unforgettable mission trips at our Rescue Center. Come share in these children\'s journeys, form meaningful connections, and see the lasting impact your time and love can make. This is more than a trip, this is an opportunity to bring hope, change lives, and be changed yourself forever.'
                : 'Damos la bienvenida a equipos durante todo el año para unirse a nosotros en Colombia en viajes misioneros inolvidables en nuestro Centro de Rescate. Ven a compartir en los caminos de estos niños, forma conexiones significativas, y ve el impacto duradero que tu tiempo y amor pueden hacer. Esto es más que un viaje, es una oportunidad de traer esperanza, cambiar vidas, y ser cambiado tú mismo para siempre.'}
            </p>
          </motion.div>

          {/* Activity cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl mx-auto mb-4">
            {MISSION_ACTIVITIES.map((activity, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="flex items-start gap-4 p-5 rounded-xl border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04] transition-colors duration-500"
                data-testid={`mission-activity-${i}`}
              >
                <div className="w-10 h-10 rounded-lg bg-sky-400/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <activity.icon className="w-5 h-5 text-sky-400/80" />
                </div>
                <p className="text-sm sm:text-base text-white/50 leading-relaxed">
                  {en ? activity.en : activity.es}
                </p>
              </motion.div>
            ))}
          </div>

          {/* CTA for mission trips */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-center"
          >
            <Link
              to="/get-involved"
              className="inline-flex items-center gap-3 px-8 py-3.5 border border-sky-400/30 text-sky-300 rounded-full text-sm font-semibold hover:bg-sky-400/10 hover:border-sky-400/50 transition-all duration-500"
              data-testid="mission-trip-cta"
            >
              <MapPin className="w-4 h-4" />
              {en ? 'Plan Your Mission Trip' : 'Planifica Tu Viaje Misionero'}
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          SECTION 4 — EDGE-TO-EDGE PHOTO COLLAGE
      ══════════════════════════════════════════════ */}
      {photos.length > 0 && (
        <section className="relative" data-testid="heros-collage-section">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-400/20 to-transparent" />

          {/* Section heading */}
          <div className="text-center py-8 bg-[#0a0a0a]">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-[0.15em] uppercase text-amber-400/50 font-black">
              {en ? 'Moments of Impact' : 'Momentos de Impacto'}
            </h2>
          </div>

          {/* Edge-to-edge masonry — zero padding, minimal gap */}
          <div
            className="columns-2 sm:columns-3 md:columns-4 lg:columns-5 gap-0"
            data-testid="heros-photo-grid"
            style={{ columnGap: '2px' }}
          >
            {photos.map((photo, i) => (
              <div
                key={photo.id || i}
                className="break-inside-avoid cursor-pointer relative overflow-hidden group"
                onClick={() => setLightboxPhoto(photo)}
                style={{ marginBottom: '2px' }}
                data-testid={`heros-photo-${i}`}
              >
                <img
                  src={photo.url}
                  alt=""
                  loading="lazy"
                  className="w-full block transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-500" />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ══════════════════════════════════════════════
          SECTION 5 — IMPACT STATEMENT + DONATE CTA
      ══════════════════════════════════════════════ */}
      <section className="relative py-14 md:py-20" data-testid="heros-cta-section">
        <div className="absolute inset-0 bg-[#0a0a0a]" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-400/20 to-transparent" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-amber-500/[0.04] blur-[130px]" />

        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 1 }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black leading-tight mb-6" data-testid="cta-headline">
              <span className="text-white/90">{en ? 'Be Part of' : 'Sé Parte de'}</span>
              <br />
              <span className="bg-gradient-to-r from-amber-300 to-orange-400 bg-clip-text text-transparent">
                {en ? 'Something Eternal' : 'Algo Eterno'}
              </span>
            </h2>

            <p className="text-base sm:text-lg text-white/45 leading-relaxed mb-10 max-w-xl mx-auto">
              {en
                ? 'Every child rescued is a life that will never be the same. Your generosity becomes their safety, their education, their future. This is more than charity — this is destiny.'
                : 'Cada niño rescatado es una vida que nunca será igual. Su generosidad se convierte en su seguridad, su educación, su futuro. Esto es más que caridad — esto es destino.'}
            </p>

            <Link
              to="/donate"
              className="inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-amber-400 to-orange-500 text-black font-bold rounded-full text-base hover:shadow-[0_0_40px_rgba(251,191,36,0.25)] transition-all duration-500 hover:scale-105"
              data-testid="heros-donate-cta"
            >
              <Heart className="w-5 h-5" fill="currentColor" />
              {en ? 'Become a Hero Today' : 'Conviértete en un Héroe Hoy'}
            </Link>

            <p className="mt-6 text-xs text-white/25 tracking-wide">
              {en ? '100% of donations go directly to rescuing children' : '100% de las donaciones van directamente a rescatar niños'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ LIGHTBOX ═══════════════ */}
      <AnimatePresence>
        {lightboxPhoto && (
          <motion.div
            key="heros-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center p-4 cursor-pointer"
            onClick={() => setLightboxPhoto(null)}
            data-testid="heros-lightbox"
          >
            <button
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center transition-colors"
              onClick={(e) => { e.stopPropagation(); setLightboxPhoto(null); }}
              data-testid="heros-lightbox-close"
            >
              <X className="w-5 h-5 text-white" />
            </button>
            <motion.img
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              src={lightboxPhoto.url}
              alt=""
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
              data-testid="heros-lightbox-image"
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bottom gradient fade */}
      <div className="h-24 bg-gradient-to-b from-[#0a0a0a] to-[#0a0a0a]" />
    </div>
  );
};

export default HerosPage;
